import { useId, useState } from 'react';
import { SearchIcon, UserIcon } from 'lucide-react';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchCustomers } from '../adminApi';
import { btnGhost, btnSecondary, inputCls } from '../ui/styles';
import { contactHint, customerSearchText } from '../customers/customerRules';
import type { SaleCustomer } from './useSaleComposer';
type Props = {
  value: SaleCustomer | null;
  onChange: (c: SaleCustomer | null) => void;
};
const MAX_RESULTS = 6;

/** Voor wie de bon is: een klant met account (dan telt de kuur en komt de bon in haar dossier), of zonder account. */
export function CustomerPicker({
  value,
  onChange
}: Props) {
  const searchId = useId();
  const [query, setQuery] = useState('');
  const [guest, setGuest] = useState(false);
  const [guestName, setGuestName] = useState('');
  const {
    data,
    status,
    reload
  } = useLatestLoad(fetchCustomers);
  if (value) {
    return <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-sand px-3 py-2.5" data-id="element-2196" data-mp="3olh6ansx06oo">
        <p className="flex items-center gap-2 text-[14px] text-ink" data-id="element-2197" data-mp="3fj8zbdn12phm">
          <UserIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2198" data-mp="3ajwawogidacx" />
          {value.name || 'Losse klant'}
          {!value.id && <span className="text-[12px] text-muted" data-id="element-2199" data-mp="3p9lfyurq1yih">· zonder account</span>}
        </p>
        <button type="button" onClick={() => onChange(null)} className={btnGhost} data-id="element-2200" data-mp="3kgsdyenn14a3">
          Andere klant
        </button>
      </div>;
  }
  if (guest) {
    return <div className="space-y-2" data-id="element-2201" data-mp="3u853v0pvlkdh">
        <label htmlFor={`${searchId}-guest`} className="block text-[13px] text-ink" data-id="element-2202" data-mp="3u6u2t8nteqq2">
          Naam op de bon <span className="text-muted" data-id="element-2203" data-mp="c0z6epbab356">(mag leeg)</span>
        </label>
        <div className="flex flex-wrap gap-2" data-id="element-2204" data-mp="73p7mj209f4o">
          <input id={`${searchId}-guest`} value={guestName} onChange={e => setGuestName(e.target.value)} maxLength={80} className={`${inputCls} sm:max-w-xs`} data-id="element-2205" data-mp="70unksmwlwbs" />
          <button type="button" onClick={() => onChange({
          name: guestName.trim()
        })} className={btnSecondary} data-id="element-2206" data-mp="28vc1qth71q5d">
            Verder
          </button>
          <button type="button" onClick={() => setGuest(false)} className={btnGhost} data-id="element-2207" data-mp="hpz5cgq0zc3r">
            Toch een klant zoeken
          </button>
        </div>
      </div>;
  }
  const term = query.trim().toLowerCase();
  const results = term ? (data ?? []).filter(c => customerSearchText(c).includes(term)).slice(0, MAX_RESULTS) : [];
  return <div className="space-y-2" data-id="element-2208" data-mp="voyt7m5prmyv">
      <label htmlFor={searchId} className="block text-[13px] text-ink" data-id="element-2209" data-mp="vnxgld91usf1">
        Klant
      </label>
      <div className="relative" data-id="element-2210" data-mp="10mi5whmrjrop">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-2211" data-mp="15f1l1j13muvm" />
        <input id={searchId} type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Naam of e-mail" autoComplete="off" className={`${inputCls} pl-9`} data-id="element-2212" data-mp="19gdfc1ux44a5" />
      </div>
      {status === 'error' ? <p className="text-[13px] text-clay" data-id="element-2213" data-mp="1th6hv95iyyz7">
          Klanten ophalen lukte niet.{' '}
          <button type="button" onClick={reload} className="underline underline-offset-2" data-id="element-2214" data-mp="1oiv7mxbbimgg">
            Opnieuw
          </button>
        </p> : term && <ul className="divide-y divide-line overflow-hidden rounded-md border border-line bg-white" aria-label="Gevonden klanten" data-id="element-2215" data-mp="1fkjlb7hhiatt">
            {status === 'loading' && !data ? <li className="px-3 py-2.5 text-[13px] text-muted" data-id="element-2216" data-mp="27aea931fjyhy">Zoeken…</li> : results.length === 0 ? <li className="px-3 py-2.5 text-[13px] text-muted" data-id="element-2217" data-mp="22imu7mfwhb62">Geen klant gevonden.</li> : results.map(c => <li key={c.id} data-id="element-2218" data-mp="1ygjeglm77kez">
                  <button type="button" onClick={() => onChange({
          id: c.id,
          name: `${c.firstName} ${c.lastName}`.trim(),
          isNew: !c.acquisitionChannel && !c.lastVisit && !c.lastOrder
        })} className="flex w-full items-baseline justify-between gap-3 px-3 py-2.5 text-left transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-2219" data-mp="2v0idvaemby8y">
                    <span className="text-[14px] text-ink" data-id="element-2220" data-mp="21sx4smpx3r9k">
                      {c.firstName} {c.lastName}
                    </span>
                    <span className="truncate text-[12px] text-muted" data-id="element-2221" data-mp="2tia2y15yzsx6">{contactHint(c)}</span>
                  </button>
                </li>)}
          </ul>}
      <button type="button" onClick={() => setGuest(true)} className={`${btnGhost} -ml-2.5`} data-id="element-2222" data-mp="2kpya68ndvte1">
        Zonder account afrekenen
      </button>
    </div>;
}